import { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import apiService from '../services/api'

const AskOrinModal = ({ isOpen, onClose, idea, onIdeaUpdated }) => {
  const [messages, setMessages] = useState([])
  const [inputValue, setInputValue] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [showMobileNav, setShowMobileNav] = useState(false)
  const messagesEndRef = useRef(null)

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      // Generate initial context message from Orin
      const score = idea?.opportunity_assessment?.score
      const scoreText = score >= 7 ? 'promising' : score >= 5 ? 'with potential' : 'that needs work'

      setMessages([{
        role: 'assistant',
        content: `I've analyzed your idea and scored it ${score}/10 - ${scoreText}. Ask me anything about my assessment, the market research, or how you might refine the idea. I'm here to help you understand the opportunity better!`
      }])
    }
  }, [isOpen, messages.length, idea])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  if (!isOpen) return null

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!inputValue.trim() || isLoading) return

    const userMessage = inputValue.trim()
    setInputValue('')
    setMessages(prev => [...prev, { role: 'user', content: userMessage }])
    setIsLoading(true)

    try {
      // Build context about the idea and assessment
      const context = {
        idea: {
          what: idea.what,
          who: idea.who,
          why: idea.why,
          what_expected: idea.what_expected,
          make_money: idea.make_money
        },
        assessment: idea.opportunity_assessment,
        market_demand: idea.market_demand_evaluation,
        market_saturation: idea.market_saturation_evaluation,
        usp_evaluation: idea.usp_evaluation
      }

      const response = await apiService.askOrin({
        message: userMessage,
        history: messages,
        context,
        ideaId: idea.id
      })

      setMessages(prev => [...prev, { role: 'assistant', content: response.message }])

      // If Orin suggests updating the idea and provides updates
      if (response.idea_updates && onIdeaUpdated) {
        onIdeaUpdated(response.idea_updates)
      }
    } catch (err) {
      console.error('Ask Orin error:', err)
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "I'm having trouble processing that right now. Could you try asking again?"
      }])
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    setMessages([])
    setInputValue('')
    onClose()
  }

  const basePath = import.meta.env.BASE_URL

  // Quick question suggestions based on score
  const quickQuestions = idea?.opportunity_assessment?.score >= 7
    ? [
        "What should I focus on first?",
        "How can I validate this idea quickly?",
        "What's the biggest risk I should address?"
      ]
    : idea?.opportunity_assessment?.score >= 5
    ? [
        "How can I improve my idea's score?",
        "What's missing from my value proposition?",
        "Who else should I consider targeting?"
      ]
    : [
        "Why did my idea score low?",
        "How can I pivot this idea?",
        "What would make this more viable?"
      ]

  return (
    <>
      {/* Mobile Layout - Full screen with avatar in messages */}
      <div className="fixed inset-0 z-50 flex flex-col bg-gray-900 sm:hidden">
        {/* Mobile Header Row 1 - Opptora branding + nav menu */}
        <div className="flex-shrink-0 bg-gray-800 border-b border-white/10 relative">
          <div className="flex h-12 items-center justify-between px-4">
            <div className="flex items-center gap-2">
              <img src={`${basePath}images/logo.png`} alt="Opptora" className="w-6 h-6" />
              <span className="text-white font-semibold text-base">Opptora</span>
            </div>
            {/* Nav menu button */}
            <button
              onClick={() => setShowMobileNav(!showMobileNav)}
              className="text-gray-400 hover:text-white transition-colors p-1"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>

          {/* Mobile Nav Dropdown */}
          {showMobileNav && (
            <div className="absolute top-full right-0 left-0 bg-gray-800 border-b border-white/10 shadow-lg z-50">
              <nav className="py-2">
                <a href="/app" className="block px-4 py-3 text-gray-300 hover:bg-gray-700 hover:text-white transition-colors">
                  Dashboard
                </a>
                <a href="/app" className="block px-4 py-3 text-gray-300 hover:bg-gray-700 hover:text-white transition-colors">
                  My Ideas
                </a>
                <button
                  onClick={handleClose}
                  className="w-full text-left px-4 py-3 text-gray-300 hover:bg-gray-700 hover:text-white transition-colors"
                >
                  Close Chat
                </button>
              </nav>
            </div>
          )}
        </div>

        {/* Mobile Header Row 2 - Chat context + close */}
        <div className="flex-shrink-0 bg-gray-800/50 border-b border-white/10">
          <div className="flex h-11 items-center justify-between px-4">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full overflow-hidden bg-gray-700 flex items-center justify-center">
                <img
                  src={`${basePath}characters/Orin/1_Default/1_thumbup.png`}
                  alt="Orin"
                  className="w-full h-full object-cover object-top scale-[1.8] translate-y-1.5"
                />
              </div>
              <span className="text-white text-sm font-medium">Chatting with Orin</span>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-white transition-colors p-1"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Quick question chips for mobile */}
        {messages.length <= 1 && (
          <div className="flex-shrink-0 px-4 py-3 border-b border-white/10 bg-gray-800/30">
            <div className="flex flex-wrap gap-1.5 justify-center">
              {quickQuestions.map((question, idx) => (
                <button
                  key={idx}
                  onClick={() => setInputValue(question)}
                  className="px-2.5 py-1.5 text-xs bg-cyan-900/30 text-cyan-300 rounded-full border border-cyan-700/50 hover:bg-cyan-800/40 transition-colors"
                >
                  {question}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Mobile Messages - with avatar */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {messages.map((message, index) => (
            <div key={index} className={`flex gap-2 ${message.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
              {message.role === 'assistant' && (
                <div className="w-8 h-8 flex-shrink-0 rounded-full overflow-hidden bg-gray-700 flex items-center justify-center">
                  <img
                    src={`${basePath}characters/Orin/1_Default/1_thumbup.png`}
                    alt="Orin"
                    className="w-full h-full object-cover object-top scale-[1.8] translate-y-2"
                  />
                </div>
              )}
              <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl ${
                message.role === 'user'
                  ? 'bg-cyan-600 text-white'
                  : 'bg-gray-800 text-gray-100'
              }`}>
                <p className="text-sm whitespace-pre-wrap">{message.content}</p>
              </div>
            </div>
          ))}
          {isLoading && (
            <div className="flex gap-2">
              <div className="w-8 h-8 flex-shrink-0 rounded-full overflow-hidden bg-gray-700 flex items-center justify-center">
                <img
                  src={`${basePath}characters/Orin/1_Default/1_thumbup.png`}
                  alt="Orin"
                  className="w-full h-full object-cover object-top scale-[1.8] translate-y-2"
                />
              </div>
              <div className="px-4 py-3 rounded-2xl bg-gray-800">
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Mobile Input */}
        <div className="flex-shrink-0 px-4 py-3 border-t border-white/10 bg-gray-800">
          <form onSubmit={handleSubmit} className="flex gap-2">
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="Ask about your assessment..."
              className="flex-1 px-4 py-3 bg-gray-900/50 border border-gray-600 rounded-xl text-white text-base placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 disabled:opacity-50"
              disabled={isLoading}
            />
            <button
              type="submit"
              disabled={isLoading || !inputValue.trim()}
              className="px-4 py-3 bg-cyan-600 text-white rounded-xl hover:bg-cyan-700 transition-colors disabled:opacity-50 flex-shrink-0"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            </button>
          </form>
        </div>
      </div>

      {/* Desktop Layout - Modal with floating character */}
      <div className="hidden sm:block fixed inset-0 z-50 overflow-hidden">
        <div className="flex items-start justify-center h-screen px-4 text-center overflow-hidden pt-[200px]">
          <div className="fixed inset-0 bg-gray-900/80 transition-opacity" onClick={handleClose} />

          <div className="relative inline-block bg-gray-800 rounded-2xl text-left overflow-visible shadow-xl transform transition-all w-full max-w-2xl border border-white/10 my-8">
            {/* Floating character for desktop */}
            <div className="absolute -top-48 left-1/2 -translate-x-1/2 w-64 h-64 z-50">
              <img
                src={`${basePath}characters/Orin/1_Default/1_thumbup.png`}
                alt="Orin"
                className="w-full h-full object-contain drop-shadow-2xl"
              />
            </div>

            <button
              onClick={handleClose}
              className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors z-10"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div className="px-6 pt-20 pb-6">
              <div className="text-center mb-4">
                <h2 className="text-2xl font-light text-white mb-1" style={{ fontFamily: 'var(--font-handwritten)' }}>
                  Let&apos;s dig deeper...
                </h2>
                <p className="text-gray-400 text-sm">
                  Ask me anything about your assessment
                </p>
              </div>

              {/* Quick question chips */}
              {messages.length <= 1 && (
                <div className="flex flex-wrap gap-2 mb-4 justify-center">
                  {quickQuestions.map((question, idx) => (
                    <button
                      key={idx}
                      onClick={() => setInputValue(question)}
                      className="px-3 py-1.5 text-xs bg-cyan-900/30 text-cyan-300 rounded-full border border-cyan-700/50 hover:bg-cyan-800/40 transition-colors"
                    >
                      {question}
                    </button>
                  ))}
                </div>
              )}

              {/* Messages */}
              <div className="h-64 overflow-y-auto mb-4 space-y-3 pr-2">
                {messages.map((message, index) => (
                  <div
                    key={index}
                    className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] px-4 py-2 rounded-xl ${
                        message.role === 'user'
                          ? 'bg-cyan-600 text-white'
                          : 'bg-gray-700 text-gray-200'
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                    </div>
                  </div>
                ))}
                {isLoading && (
                  <div className="flex justify-start">
                    <div className="bg-gray-700 text-gray-200 px-4 py-2 rounded-xl">
                      <div className="flex items-center space-x-1">
                        <div className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                        <div className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                        <div className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              <form onSubmit={handleSubmit} className="flex gap-2">
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder="Ask about your assessment..."
                  className="flex-1 px-4 py-2 border border-gray-600 rounded-xl shadow-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 bg-gray-900/50 text-white"
                  disabled={isLoading}
                />
                <button
                  type="submit"
                  disabled={isLoading || !inputValue.trim()}
                  className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

AskOrinModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  idea: PropTypes.object.isRequired,
  onIdeaUpdated: PropTypes.func
}

export default AskOrinModal
